import React, { useState, useEffect } from "react";
import {
  Calendar,
  Plus,
  Clock,
  MapPin,
  Trash2,
  Check,
  X,
  AlertTriangle,
  FileText,
  Settings,
  RefreshCw,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  reuniaoService,
  ReuniaoAgendada,
  CronogramaConfig,
} from "../services/reuniaoService";
import { mandatoService, MandatoComNomes } from "../services/mandatoService";
import { notifyError, notifySuccess, notifyInfo } from "../utils/notify";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export const CronogramaReunioes: React.FC = () => {
  const { user, empresaSelecionada } = useAuth();
  const [reunioes, setReunioes] = useState<ReuniaoAgendada[]>([]);
  const [mandatos, setMandatos] = useState<MandatoComNomes[]>([]);
  const [mandatoSelecionado, setMandatoSelecionado] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [mostrarModalConfig, setMostrarModalConfig] = useState(false);
  const [mostrarModalExtraordinaria, setMostrarModalExtraordinaria] =
    useState(false);
  const [mostrarDialogExcluir, setMostrarDialogExcluir] = useState(false);
  const [reuniaoParaExcluir, setReuniaoParaExcluir] = useState<string | null>(
    null,
  );

  const [config, setConfig] = useState<CronogramaConfig>({
    frequencia: "mensal",
    dia_preferencial: 15,
    horario_inicio: "14:00",
    duracao_horas: 2,
    local_padrao: "Sala de Reuniões",
    considerar_feriados: true,
    considerar_fins_semana: true,
  });

  const [reuniaoExtraordinaria, setReuniaoExtraordinaria] = useState({
    data: new Date().toISOString().split("T")[0],
    horario: "14:00",
    local: "Sala de Reuniões",
    pauta: "",
  });

  const carregarDados = async () => {
    if (!empresaSelecionada?.id) return;

    try {
      setLoading(true);
      const mandatosData = await mandatoService.listar(empresaSelecionada.id);
      setMandatos(mandatosData);

      // Selecionar mandato ativo por padrão
      const mandatoAtivo = mandatosData.find((m) => m.status === "ativo");
      if (mandatoAtivo?.id) {
        setMandatoSelecionado(mandatoAtivo.id);
      } else if (mandatosData.length > 0 && mandatosData[0].id) {
        setMandatoSelecionado(mandatosData[0].id);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      notifyError("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const carregarReunioesMandato = async (mandatoId: string) => {
    try {
      const reunioesData =
        await reuniaoService.listarReunioesPorMandato(mandatoId);
      setReunioes(reunioesData);
    } catch (error) {
      console.error("Erro ao carregar reuniões:", error);
      notifyError("Erro ao carregar reuniões");
    }
  };

  useEffect(() => {
    if (empresaSelecionada?.id) {
      carregarDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaSelecionada]);

  useEffect(() => {
    if (mandatoSelecionado) {
      carregarReunioesMandato(mandatoSelecionado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandatoSelecionado]);

  const gerarCronograma = async () => {
    if (!empresaSelecionada?.id || !user?.id || !mandatoSelecionado) {
      notifyError("Selecione um mandato");
      return;
    }

    try {
      const mandato = mandatos.find((m) => m.id === mandatoSelecionado);
      if (!mandato) {
        notifyError("Mandato não encontrado");
        return;
      }

      // Verificar se já existe cronograma
      if (reunioes.length > 0) {
        const confirmar = window.confirm(
          "Já existe um cronograma para este mandato. Deseja substituí-lo?",
        );
        if (!confirmar) return;

        // Excluir reuniões não realizadas
        const reunioesNaoRealizadas = reunioes.filter(
          (r) => r.status !== "realizada",
        );
        await Promise.all(
          reunioesNaoRealizadas.map((r) =>
            reuniaoService.excluirReuniao(r.id!),
          ),
        );
      }

      notifyInfo("Gerando cronograma... Aguarde.");

      const novasReunioes = await reuniaoService.gerarCronogramaAnual(
        empresaSelecionada.id,
        mandatoSelecionado,
        mandato.data_inicio,
        mandato.data_fim,
        config,
        user.id,
      );

      await reuniaoService.salvarReunioes(
        novasReunioes,
        empresaSelecionada.id,
        user.id,
        true, // Criar atas automaticamente
      );
      notifySuccess(
        `Cronograma gerado com ${novasReunioes.length} reuniões e atas criadas!`,
      );
      setMostrarModalConfig(false);
      carregarReunioesMandato(mandatoSelecionado);
    } catch (error) {
      console.error("Erro ao gerar cronograma:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao gerar cronograma";
      notifyError(errorMessage);
    }
  };

  const criarExtraordinaria = async () => {
    if (!empresaSelecionada?.id || !user?.id || !mandatoSelecionado) {
      notifyError("Selecione um mandato");
      return;
    }

    if (!reuniaoExtraordinaria.pauta) {
      notifyError("Informe a pauta da reunião");
      return;
    }

    try {
      await reuniaoService.criarReuniaoExtraordinaria(
        empresaSelecionada.id,
        mandatoSelecionado,
        reuniaoExtraordinaria.data,
        reuniaoExtraordinaria.horario,
        reuniaoExtraordinaria.local,
        reuniaoExtraordinaria.pauta,
        user.id,
      );

      notifySuccess("Reunião extraordinária criada!");
      setMostrarModalExtraordinaria(false);
      setReuniaoExtraordinaria({
        data: new Date().toISOString().split("T")[0],
        horario: "14:00",
        local: "Sala de Reuniões",
        pauta: "",
      });
      carregarReunioesMandato(mandatoSelecionado);
    } catch (error) {
      console.error("Erro ao criar reunião:", error);
      notifyError("Erro ao criar reunião extraordinária");
    }
  };

  const confirmarExclusao = (id: string) => {
    setReuniaoParaExcluir(id);
    setMostrarDialogExcluir(true);
  };

  const excluirReuniao = async () => {
    if (!reuniaoParaExcluir) return;

    try {
      await reuniaoService.excluirReuniao(reuniaoParaExcluir);
      notifySuccess("Reunião excluída com sucesso!");
      setMostrarDialogExcluir(false);
      setReuniaoParaExcluir(null);
      if (mandatoSelecionado) {
        carregarReunioesMandato(mandatoSelecionado);
      }
    } catch (error) {
      console.error("Erro ao excluir reunião:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao excluir reunião";
      notifyError(errorMessage);
    }
  };

  const cancelarReuniao = async (id: string) => {
    const motivo = prompt("Informe o motivo do cancelamento:");
    if (!motivo) return;

    try {
      await reuniaoService.cancelarReuniao(id, motivo);
      notifySuccess("Reunião cancelada!");
      if (mandatoSelecionado) {
        carregarReunioesMandato(mandatoSelecionado);
      }
    } catch (error) {
      console.error("Erro ao cancelar reunião:", error);
      notifyError("Erro ao cancelar reunião");
    }
  };

  const remarcarReuniao = async (id: string, dataAtual: string) => {
    const novaDataStr = prompt(
      "Informe a nova data (formato AAAA-MM-DD):",
      dataAtual,
    );
    if (!novaDataStr) return;

    const motivo = prompt("Informe o motivo da remarcação:");
    if (!motivo) return;

    try {
      await reuniaoService.remarcarReuniao(id, novaDataStr, motivo);
      notifySuccess("Reunião remarcada!");
      if (mandatoSelecionado) {
        carregarReunioesMandato(mandatoSelecionado);
      }
    } catch (error) {
      console.error("Erro ao remarcar reunião:", error);
      notifyError("Erro ao remarcar reunião");
    }
  };

  const getStatusBadge = (status: ReuniaoAgendada["status"]) => {
    switch (status) {
      case "realizada":
        return (
          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1">
            <Check className="w-3 h-3" />
            Realizada
          </span>
        );
      case "agendada":
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Agendada
          </span>
        );
      case "cancelada":
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center gap-1">
            <X className="w-3 h-3" />
            Cancelada
          </span>
        );
      case "remarcada":
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center gap-1">
            <RefreshCw className="w-3 h-3" />
            Remarcada
          </span>
        );
    }
  };

  const getTipoBadge = (tipo: ReuniaoAgendada["tipo"]) => {
    return tipo === "ordinaria" ? (
      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
        Ordinária
      </span>
    ) : (
      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
        Extraordinária
      </span>
    );
  };

  const reunioesPorMes = reunioes.reduce(
    (acc, reuniao) => {
      const mes = reuniao.data_prevista.substring(0, 7); // AAAA-MM
      if (!acc[mes]) acc[mes] = [];
      acc[mes].push(reuniao);
      return acc;
    },
    {} as Record<string, ReuniaoAgendada[]>,
  );

  const mesesOrdenados = Object.keys(reunioesPorMes).sort();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <Clock className="w-8 h-8 text-blue-600 animate-pulse mx-auto mb-2" />
          <p className="text-gray-600">Carregando cronograma...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Cronograma de Reuniões da CIPA
            </h1>
            <p className="text-blue-100">{empresaSelecionada?.nome}</p>
            <p className="text-sm text-blue-200 mt-1">
              Conforme NR-05 - Reuniões Ordinárias e Extraordinárias
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setMostrarModalConfig(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Settings className="w-4 h-4" />
              Gerar Cronograma
            </button>
            <button
              onClick={() => setMostrarModalExtraordinaria(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Reunião Extraordinária
            </button>
          </div>
        </div>
      </div>

      {/* Seletor de Mandato */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Selecione o Mandato
        </label>
        <select
          value={mandatoSelecionado || ""}
          onChange={(e) => setMandatoSelecionado(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg"
        >
          <option value="">Selecione...</option>
          {mandatos.map((m) => (
            <option key={m.id} value={m.id}>
              Mandato #{m.numero_mandato} - {m.status} (
              {new Date(m.data_inicio).toLocaleDateString("pt-BR")} a{" "}
              {new Date(m.data_fim).toLocaleDateString("pt-BR")})
            </option>
          ))}
        </select>
      </div>

      {/* Reuniões por Mês */}
      {!mandatoSelecionado ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">
            Selecione um mandato para visualizar o cronograma
          </p>
        </div>
      ) : reunioes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-2">
            Nenhuma reunião agendada para este mandato
          </p>
          <button
            onClick={() => setMostrarModalConfig(true)}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            Gerar cronograma anual
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {mesesOrdenados.map((mes) => {
            const [ano, mesNum] = mes.split("-");
            const nomeMes = new Date(parseInt(ano), parseInt(mesNum) - 1, 1)
              .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
              .replace(/^\w/, (c) => c.toUpperCase());

            return (
              <div key={mes} className="bg-white rounded-lg shadow-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  {nomeMes}
                </h3>

                <div className="space-y-3">
                  {reunioesPorMes[mes].map((reuniao) => (
                    <div
                      key={reuniao.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">
                              {reuniao.pauta ||
                                `${reuniao.numero}ª Reunião ${reuniao.tipo === "ordinaria" ? "Ordinária" : "Extraordinária"}`}
                            </h4>
                            {getTipoBadge(reuniao.tipo)}
                            {getStatusBadge(reuniao.status)}
                          </div>
                        </div>

                        {reuniao.status === "agendada" && (
                          <div className="flex gap-1">
                            <button
                              onClick={() =>
                                remarcarReuniao(
                                  reuniao.id!,
                                  reuniao.data_prevista,
                                )
                              }
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded"
                              title="Remarcar"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => cancelarReuniao(reuniao.id!)}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded"
                              title="Cancelar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => confirmarExclusao(reuniao.id!)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {new Date(reuniao.data_prevista).toLocaleDateString(
                              "pt-BR",
                              {
                                weekday: "long",
                                day: "2-digit",
                                month: "long",
                              },
                            )}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>
                            {reuniao.horario_inicio}
                            {reuniao.horario_fim && ` - ${reuniao.horario_fim}`}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{reuniao.local}</span>
                        </div>
                      </div>

                      {reuniao.ata_id && (
                        <div className="mt-2 pt-2 border-t">
                          <a
                            href={`#/atas/${reuniao.ata_id}`}
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            Ver ata da reunião
                            <ChevronRight className="w-3 h-3" />
                          </a>
                        </div>
                      )}

                      {reuniao.motivo_cancelamento && (
                        <div className="mt-2 text-sm text-gray-600 bg-yellow-50 p-2 rounded">
                          <strong>Motivo:</strong> {reuniao.motivo_cancelamento}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Configuração Cronograma */}
      {mostrarModalConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Configurar Cronograma Anual
                </h3>
                <button
                  onClick={() => setMostrarModalConfig(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    <strong>NR-05:</strong> Reuniões ordinárias mensais ou
                    bimestrais para ME/EPP com risco 1 e 2.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequência
                    </label>
                    <select
                      value={config.frequencia}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          frequencia: e.target.value as "mensal" | "bimestral",
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    >
                      <option value="mensal">Mensal</option>
                      <option value="bimestral">Bimestral</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dia Preferencial
                    </label>
                    <input
                      type="number"
                      value={config.dia_preferencial}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          dia_preferencial: parseInt(e.target.value),
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      min="1"
                      max="31"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Horário de Início
                    </label>
                    <input
                      type="time"
                      value={config.horario_inicio}
                      onChange={(e) =>
                        setConfig({ ...config, horario_inicio: e.target.value })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duração (horas)
                    </label>
                    <input
                      type="number"
                      value={config.duracao_horas}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          duracao_horas: parseFloat(e.target.value),
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      min="0.5"
                      max="8"
                      step="0.5"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Local Padrão
                  </label>
                  <input
                    type="text"
                    value={config.local_padrao}
                    onChange={(e) =>
                      setConfig({ ...config, local_padrao: e.target.value })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="considerar_feriados"
                      checked={config.considerar_feriados}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          considerar_feriados: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600"
                    />
                    <label
                      htmlFor="considerar_feriados"
                      className="text-sm font-medium text-gray-700"
                    >
                      Evitar feriados nacionais
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="considerar_fins_semana"
                      checked={config.considerar_fins_semana}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          considerar_fins_semana: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600"
                    />
                    <label
                      htmlFor="considerar_fins_semana"
                      className="text-sm font-medium text-gray-700"
                    >
                      Evitar fins de semana
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setMostrarModalConfig(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={gerarCronograma}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Gerar Cronograma
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reunião Extraordinária */}
      {mostrarModalExtraordinaria && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  Nova Reunião Extraordinária
                </h3>
                <button
                  onClick={() => setMostrarModalExtraordinaria(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    <strong>NR-05:</strong> Reuniões extraordinárias podem ser
                    convocadas por qualquer membro ou após acidente grave/fatal.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data *
                  </label>
                  <input
                    type="date"
                    value={reuniaoExtraordinaria.data}
                    onChange={(e) =>
                      setReuniaoExtraordinaria({
                        ...reuniaoExtraordinaria,
                        data: e.target.value,
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário *
                  </label>
                  <input
                    type="time"
                    value={reuniaoExtraordinaria.horario}
                    onChange={(e) =>
                      setReuniaoExtraordinaria({
                        ...reuniaoExtraordinaria,
                        horario: e.target.value,
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Local *
                  </label>
                  <input
                    type="text"
                    value={reuniaoExtraordinaria.local}
                    onChange={(e) =>
                      setReuniaoExtraordinaria({
                        ...reuniaoExtraordinaria,
                        local: e.target.value,
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pauta *
                  </label>
                  <textarea
                    value={reuniaoExtraordinaria.pauta}
                    onChange={(e) =>
                      setReuniaoExtraordinaria({
                        ...reuniaoExtraordinaria,
                        pauta: e.target.value,
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    rows={4}
                    placeholder="Descreva o motivo da reunião extraordinária..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setMostrarModalExtraordinaria(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={criarExtraordinaria}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Criar Reunião
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Confirmação Exclusão */}
      <AlertDialog
        open={mostrarDialogExcluir}
        onOpenChange={setMostrarDialogExcluir}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta reunião? Esta ação não pode
              ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={excluirReuniao}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
